'use client'

import { ModalSizes } from './types/modalTypes'
import { DEFAULT_MODAL_SIZE, MODAL_ROOT_ID } from './constants'
import { useModalVisibilityCallbacks } from './hooks/useModalVisibilityCallbacks'
import { ModalContent } from './ModalContent'
import { useIsClient } from '../../../hooks/useIsClient'
import { useModalClickOutside } from './hooks/useModalClickOutside'
import React, { useRef } from 'react'
import { createPortal } from 'react-dom'

export interface ModalProps {
    title?: string | React.ReactNode
    size?: ModalSizes
    children?: React.ReactNode
    footer?: React.ReactNode
    /** Place component next to modal title, i.e. a language switch */
    headerComponent?: React.ReactNode
    canBeClosed?: boolean
    isModalVisible: boolean
    setModalVisibility: React.Dispatch<React.SetStateAction<boolean>>
    hasBackdrop?: boolean
    stackedButtons?: boolean
    fullContentWidth?: boolean
    onHide?: () => void
    onShow?: () => void
    className?: string
}

export const Modal = ({
    children,
    title,
    size = DEFAULT_MODAL_SIZE,
    stackedButtons,
    footer,
    headerComponent,
    canBeClosed = true,
    isModalVisible = false,
    setModalVisibility,
    hasBackdrop = true,
    fullContentWidth = false,
    onShow,
    onHide,
    className = '',
    ...props
}: ModalProps) => {
    const modalLayerRef = useRef<HTMLDivElement>(null)
    const dialogRef = useRef<HTMLDivElement>(null)

    // #modal-root lives in each app's layout.tsx; useIsClient gates DOM access until after hydration.
    const isClient = useIsClient()
    const modalRoot = isClient ? document.getElementById(MODAL_ROOT_ID) : null

    useModalVisibilityCallbacks(isModalVisible, onShow, onHide)

    useModalClickOutside(modalLayerRef, () => {
        if (isModalVisible && canBeClosed) setModalVisibility(false)
    })

    const closeModal = (): void => {
        setModalVisibility((previousValue) => !previousValue)
    }

    if (!modalRoot || !isModalVisible) {
        return null
    }

    return createPortal(
        <ModalContent
            {...props}
            title={title}
            size={size}
            stackedButtons={stackedButtons}
            footer={footer}
            headerComponent={headerComponent}
            canBeClosed={canBeClosed}
            hasBackdrop={hasBackdrop}
            fullContentWidth={fullContentWidth}
            className={className}
            modalLayerRef={modalLayerRef}
            dialogRef={dialogRef}
            onClose={closeModal}
            onBackdropClick={() => {
                if (canBeClosed) closeModal()
            }}
        >
            {children}
        </ModalContent>,
        modalRoot,
    )
}
